import React from 'react';
// Importar componentes de navegación y hooks de ubicación e historial de React Router
import { Link, useLocation, useNavigate } from 'react-router-dom';
// Importar notificaciones toast
import { toast } from 'react-hot-toast';
// Importar iconos desde heroicons para la barra de navegación lateral/superior
import { 
  HomeIcon, 
  UserPlusIcon, 
  ChartBarIcon, 
  ClipboardDocumentListIcon,
  ClockIcon,
  ArrowsRightLeftIcon,
  ArrowRightOnRectangleIcon, // Icono para el botón de Cerrar sesión
  ArrowLeftOnRectangleIcon   // Icono alternativo para inicio de sesión
} from '@heroicons/react/24/outline';

function Layout({ children }) {
  // Hook para conocer la ruta actual y aplicar estilos activos a los links
  const location = useLocation();
  // Hook de navegación para redirigir tras cerrar sesión
  const navigate = useNavigate();

  // Obtener y parsear los datos del participante desde el localStorage si está logueado
  const activeParticipantStr = localStorage.getItem('activeParticipant');
  const activeParticipant = activeParticipantStr ? JSON.parse(activeParticipantStr) : null;

  // Definición de las opciones de navegación dinámicas según el estado de la sesión
  const navigation = activeParticipant 
    ? [
        // Rutas disponibles únicamente para usuarios autenticados
        { name: 'Inicio', href: '/', icon: HomeIcon },
        { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon },
        { name: 'Tests', href: '/tests', icon: ClipboardDocumentListIcon },
        { name: 'Progreso', href: '/progress', icon: ClockIcon },
        { name: 'Comparar', href: '/comparison', icon: ArrowsRightLeftIcon },
      ]
    : [
        // Rutas disponibles para usuarios no autenticados
        { name: 'Inicio', href: '/', icon: HomeIcon },
        { name: 'Registrarse', href: '/register', icon: UserPlusIcon },
        { name: 'Iniciar Sesión', href: '/login', icon: ArrowLeftOnRectangleIcon },
      ];

  // Función auxiliar para determinar si un link de navegación coincide con la ruta activa
  const isActive = (href) => location.pathname === href;

  // Manejador del botón "Cerrar sesión"
  const handleLogout = () => {
    // Eliminar la información del participante del almacenamiento local
    localStorage.removeItem('activeParticipant');
    localStorage.removeItem('authToken');
    // Notificación toast de éxito al despedirse
    toast.success('Sesión cerrada con éxito. ¡Vuelve pronto!');
    // Redirigir al usuario a la página de bienvenida pública
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between">
      {/* Header Principal de Navegación */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Título de la Aplicación */}
            <div className="flex items-center">
              <Link to="/" className="text-xl font-extrabold text-indigo-600 tracking-wide hover:text-indigo-800 transition-colors">
                Autoconocimiento
              </Link>
            </div>
            
            {/* Controles de Navegación Dinámicos */}
            <nav className="flex space-x-6 items-center">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      isActive(item.href)
                        ? 'bg-indigo-100 text-indigo-700 shadow-sm'
                        : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {item.name}
                  </Link>
                );
              })}
              
              {/* Si hay un usuario logueado, mostrar su nombre y el botón de logout */}
              {activeParticipant && (
                <div className="flex items-center space-x-4 border-l border-gray-200 pl-4">
                  {/* Nombre del participante */}
                  <span className="text-xs font-semibold text-gray-700 bg-indigo-50 px-3 py-1.5 rounded-full">
                    {activeParticipant.name}
                  </span>
                  {/* Botón de Cerrar Sesión con micro-animaciones en hover */}
                  <button
                    onClick={handleLogout}
                    title="Cerrar Sesión"
                    className="flex items-center justify-center p-2 rounded-md text-red-600 hover:bg-red-50 hover:text-red-800 transition-all duration-200"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    <span className="ml-1 text-sm font-medium hidden sm:inline">Salir</span>
                  </button>
                </div>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Contenido Principal con Contenedor Responsivo */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full">
        {children}
      </main>

      {/* Footer Fijo */}
      <footer className="bg-white border-t border-gray-200 py-6 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-500">
            <p>© 2026 Autoconocimiento. Aplicación de evaluación psicológica y crecimiento personal.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
